import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";
import { getUserData } from "../../../../actions/commonActions";
import { assignRole, clearErrors } from "../../../../actions/adminUserActions";
import { getRoleOptions } from "../../../../actions/adminRoleActions";
import { ASSIGN_ROLE_RESET } from "../../../../constants/userConstants";
import Loader from "../../../Loader";

/* ── helpers ── */
const MODULE_LABELS = {
  dashboard: "Dashboard",
  home_setting: "Home Settings",
  admin_users: "Admin Users",
  students: "Students",
  banners: "Banners",
  cms_pages: "CMS Pages",
  email_templates: "Email Templates",
  categories: "Categories",
  subscriptions: "Subscriptions",
  discounts: "Discounts",
  student_subscriptions: "Student Subs",
  registered_subjects: "Registered Subjects",
  exams: "Exams",
  university: "Universities",
  assessments: "Assessments",
  courses: "Courses",
  subjects: "Subjects",
  questions: "Questions",
  topics: "Topics",
  theories: "Theories",
  advices: "Advices",
  news: "News",
  contact_us: "Contact Us",
  newsletter: "Newsletter",
  testimonials: "Testimonials",
  faqs: "FAQs",
  roles: "Roles",
};

const ACTION_ICONS = {
  view: "fa-eye",
  create: "fa-plus",
  edit: "fa-pencil",
  delete: "fa-trash",
  reply: "fa-reply",
};

const ACTION_COLORS = {
  view: "#3c8dbc",
  create: "#00a65a",
  edit: "#f39c12",
  delete: "#dd4b39",
  reply: "#605ca8",
};

function groupPermissions(perms) {
  const groups = {};
  perms.forEach((p) => {
    const [mod, action] = p.split(".");
    if (!groups[mod]) groups[mod] = [];
    groups[mod].push(action);
  });
  return groups;
}

function getInitials(name) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const AssignRole = ({ history, match }) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const currentUserId = match.params.id;

  const [roleId, setRoleId] = useState("");

  const { userData, loading: userLoading } = useSelector(
    (state) => state.userData || {}
  );
  const { roles: availableRoles = [] } = useSelector(
    (state) => state.rolesList || {}
  );
  const { isAssigned, loading: assigning, error } = useSelector(
    (state) => state.assignRole || {}
  );

  useEffect(() => {
    dispatch(getUserData(currentUserId));
    dispatch(getRoleOptions());
  }, [dispatch, currentUserId]);

  useEffect(() => {
    if (userData) {
      setRoleId(userData.roleId?._id || userData.roleId || "");
    }
  }, [userData]);

  useEffect(() => {
    if (error) {
      alert.error(error);
      dispatch(clearErrors());
    }
    if (isAssigned) {
      alert.success("Role assigned successfully");
      dispatch({ type: ASSIGN_ROLE_RESET });
      history.push("/admin/users");
    }
  }, [dispatch, alert, error, isAssigned, history]);

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(assignRole(currentUserId, roleId));
  };

  const userName = userData
    ? `${userData.first_name || ""} ${userData.last_name || ""}`.trim()
    : "Loading...";
  const userEmail = userData?.email || "";
  const currentRoleLabel =
    userData?.roleId?.label || userData?.roleId?.name || "No Role";
  const originalRoleId = userData?.roleId?._id || userData?.roleId || "";
  const hasChanged = roleId !== originalRoleId;

  const selectedRole = useMemo(
    () =>
      Array.isArray(availableRoles)
        ? availableRoles.find((r) => r._id === roleId) || null
        : null,
    [availableRoles, roleId]
  );

  const groupedPerms = useMemo(
    () => (selectedRole ? groupPermissions(selectedRole.permissions || []) : {}),
    [selectedRole]
  );

  return (
    <Fragment>
      {userLoading ? (
        <Loader />
      ) : (
        <Fragment>
          <div className="content-wrapper">
            {/* ── Scoped styles ── */}
            <style>{`
              .ar-avatar {
                width: 56px; height: 56px; border-radius: 50%;
                background: #3c8dbc; color: #fff; display: flex;
                align-items: center; justify-content: center;
                font-size: 20px; font-weight: 700; flex-shrink: 0;
              }
              .ar-user-card {
                display: flex; align-items: center; gap: 16px;
                padding: 16px 20px; border-radius: 4px;
                background: linear-gradient(135deg, #3c8dbc 0%, #367fa9 100%);
                color: #fff; margin-bottom: 24px;
              }
              .ar-user-card h4 { margin: 0 0 2px; font-size: 17px; font-weight: 600; }
              .ar-user-card .ar-email { opacity: .85; font-size: 13px; }
              .ar-role-badge {
                display: inline-block; padding: 2px 10px; border-radius: 12px;
                font-size: 11px; font-weight: 600; margin-top: 4px;
              }
              .ar-role-badge.purple  { background: rgba(255,255,255,.2); }
              .ar-role-badge.blue    { background: rgba(255,255,255,.25); }
              .ar-role-badge.gray    { background: rgba(255,255,255,.15); opacity:.8; }

              .ar-role-card {
                border: 2px solid #e8e8e8; border-radius: 6px;
                padding: 14px 18px; cursor: pointer; transition: .15s;
                margin-bottom: 8px; display: flex; align-items: center;
                justify-content: space-between;
              }
              .ar-role-card:hover { border-color: #3c8dbc; background: #f4faff; }
              .ar-role-card.selected { border-color: #3c8dbc; background: #eaf5fc; }
              .ar-role-card .ar-role-name { font-weight: 600; font-size: 14px; }
              .ar-role-card .ar-role-meta { color: #888; font-size: 12px; }

              .ar-perm-group {
                border: 1px solid #eee; border-radius: 4px;
                margin-bottom: 8px; overflow: hidden;
                break-inside: avoid;
              }
              .ar-perm-group-header {
                background: #f8f9fa; padding: 6px 12px;
                font-weight: 600; font-size: 12px;
                color: #555; border-bottom: 1px solid #eee;
                display: flex; align-items: center; gap: 6px;
              }
              .ar-perm-group-body {
                padding: 6px 12px; display: flex; flex-wrap: wrap; gap: 4px;
              }
              .ar-perm-tag {
                display: inline-flex; align-items: center; gap: 4px;
                padding: 2px 8px; border-radius: 3px;
                font-size: 11px; font-weight: 500;
                background: #f0f0f0; color: #555;
              }

              .ar-perms-grid { columns: 2; column-gap: 10px; }

              .ar-stat-row {
                display: flex; gap: 12px; margin-bottom: 20px;
                flex-wrap: wrap;
              }
              .ar-stat {
                flex: 1; min-width: 100px; text-align: center;
                padding: 12px 8px; border-radius: 4px;
                background: #f8f9fa; border: 1px solid #eee;
              }
              .ar-stat .ar-stat-num {
                font-size: 22px; font-weight: 700; color: #3c8dbc;
                line-height: 1;
              }
              .ar-stat .ar-stat-label { font-size: 11px; color: #999; margin-top: 4px; }

              .ar-change-indicator {
                display: inline-flex; align-items: center; gap: 6px;
                padding: 6px 14px; border-radius: 4px;
                font-size: 12px; font-weight: 500; margin-bottom: 12px;
              }
              .ar-change-indicator.changed {
                background: #fef7e0; color: #8a6d3b; border: 1px solid #f0d78e;
              }
              .ar-change-indicator.same {
                background: #eaf7ea; color: #3c763d; border: 1px solid #c3e6cb;
              }

              @media (max-width: 767px) {
                .ar-user-card { flex-direction: column; text-align: center; gap: 10px; }
                .ar-perms-grid { columns: 1; }
                .ar-stat-row { flex-direction: column; }
                .ar-actions .btn { display: block; width: 100%; margin-bottom: 8px; }
              }
              @media (min-width: 768px) and (max-width: 991px) {
                .ar-perms-grid { columns: 2; }
              }
              @media (min-width: 1200px) {
                .ar-perms-grid { columns: 3; }
              }
            `}</style>

            <section className="content-header">
              <h1>
                Assign <small>Role</small>
              </h1>
              <ol className="breadcrumb">
                <li>
                  <Link to="/admin">
                    <i className="fa fa-dashboard"></i> Dashboard
                  </Link>
                </li>
                <li>
                  <Link to="/admin/users">
                    <i className="fa fa-users"></i> Users
                  </Link>
                </li>
                <li className="active">Assign Role</li>
              </ol>
            </section>

            <section className="content">
              {/* ── User Card (full width) ── */}
              <div className="ar-user-card">
                <div className="ar-avatar">{getInitials(userName)}</div>
                <div>
                  <h4>{userName}</h4>
                  <div className="ar-email">
                    <i className="fa fa-envelope-o" style={{ marginRight: 4 }}></i>
                    {userEmail}
                  </div>
                  {userData?.isSuperAdmin ? (
                    <span className="ar-role-badge purple">
                      <i className="fa fa-star" style={{ marginRight: 3 }}></i>
                      Super Admin
                    </span>
                  ) : (
                    <span
                      className={`ar-role-badge ${
                        userData?.roleId ? "blue" : "gray"
                      }`}
                    >
                      <i className="fa fa-tag" style={{ marginRight: 3 }}></i>
                      {currentRoleLabel}
                    </span>
                  )}
                </div>
              </div>

              {userData?.isSuperAdmin ? (
                <div className="callout callout-warning">
                  <h4>
                    <i className="fa fa-exclamation-triangle"></i> Super Admin
                  </h4>
                  <p>
                    Super Admin users have all permissions by default and cannot
                    have a role assigned.
                  </p>
                  <Link to="/admin/users" className="btn btn-default btn-sm">
                    <i className="fa fa-arrow-left"></i> Back to Users
                  </Link>
                </div>
              ) : (
                <div className="row">
                  {/* ── Left: Role Selection ── */}
                  <div className="col-xs-12 col-md-5 col-lg-4">
                    <div className="box box-primary">
                      <div className="box-header with-border">
                        <h3 className="box-title">
                          <i className="fa fa-key"></i> Select Role
                        </h3>
                      </div>
                      <div className="box-body" style={{ padding: "15px 15px 5px" }}>
                        {/* No Role option */}
                        <div
                          className={`ar-role-card ${roleId === "" ? "selected" : ""}`}
                          onClick={() => setRoleId("")}
                        >
                          <div>
                            <div className="ar-role-name" style={{ color: "#999" }}>
                              <i className="fa fa-ban" style={{ marginRight: 6 }}></i>
                              No Role
                            </div>
                            <div className="ar-role-meta">Full access (legacy)</div>
                          </div>
                          {roleId === "" && (
                            <i className="fa fa-check-circle text-primary" style={{ fontSize: 18 }}></i>
                          )}
                        </div>

                        {/* Role cards */}
                        {Array.isArray(availableRoles) &&
                          availableRoles.map((r) => (
                            <div
                              key={r._id}
                              className={`ar-role-card ${roleId === r._id ? "selected" : ""}`}
                              onClick={() => setRoleId(r._id)}
                            >
                              <div>
                                <div className="ar-role-name">
                                  <i
                                    className="fa fa-shield"
                                    style={{
                                      marginRight: 6,
                                      color: roleId === r._id ? "#3c8dbc" : "#ccc",
                                    }}
                                  ></i>
                                  {r.label || r.name}
                                  {r.isSystem && (
                                    <span
                                      className="label label-default"
                                      style={{ marginLeft: 6, fontSize: 9, verticalAlign: "middle" }}
                                    >
                                      SYSTEM
                                    </span>
                                  )}
                                </div>
                                <div className="ar-role-meta">
                                  {r.permissions ? r.permissions.length : 0} permissions
                                </div>
                              </div>
                              {roleId === r._id && (
                                <i className="fa fa-check-circle text-primary" style={{ fontSize: 18 }}></i>
                              )}
                            </div>
                          ))}
                      </div>

                      {/* Actions */}
                      <div className="box-footer ar-actions">
                        {/* Change indicator */}
                        {hasChanged ? (
                          <div className="ar-change-indicator changed">
                            <i className="fa fa-exchange"></i>
                            Changing from <strong>{currentRoleLabel}</strong> to{" "}
                            <strong>{selectedRole?.label || selectedRole?.name || "No Role"}</strong>
                          </div>
                        ) : (
                          <div className="ar-change-indicator same">
                            <i className="fa fa-check"></i> No changes
                          </div>
                        )}

                        <form onSubmit={submitHandler}>
                          <button
                            type="submit"
                            className="btn btn-primary btn-flat"
                            disabled={assigning || !hasChanged}
                            style={{ minWidth: 130 }}
                          >
                            {assigning ? (
                              <>
                                <i className="fa fa-spinner fa-spin"></i> Saving...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-check"></i> Assign Role
                              </>
                            )}
                          </button>{" "}
                          <Link to="/admin/users" className="btn btn-default btn-flat">
                            Cancel
                          </Link>
                        </form>
                      </div>
                    </div>

                    {/* Quick help */}
                    <div className="box box-default collapsed-box" style={{ marginBottom: 0 }}>
                      <div
                        className="box-header with-border"
                        style={{ cursor: "pointer" }}
                        data-widget="collapse"
                      >
                        <h3 className="box-title" style={{ fontSize: 13 }}>
                          <i className="fa fa-question-circle"></i> Help
                        </h3>
                        <div className="box-tools pull-right">
                          <button className="btn btn-box-tool" data-widget="collapse">
                            <i className="fa fa-plus"></i>
                          </button>
                        </div>
                      </div>
                      <div className="box-body" style={{ fontSize: 12, color: "#777" }}>
                        <p>
                          <strong>No Role</strong> — Legacy admin users without a role
                          keep full access for backward compatibility.
                        </p>
                        <p>
                          <strong>Super Admins</strong> always bypass role checks.
                        </p>
                        <p style={{ marginBottom: 0 }}>
                          Changes take effect on the user's next login.{" "}
                          <Link to="/admin/roles">Manage Roles →</Link>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Right: Permission Preview ── */}
                  <div className="col-xs-12 col-md-7 col-lg-8">
                    <div className="box box-default">
                      <div
                        className="box-header with-border"
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}
                      >
                        <h3 className="box-title" style={{ margin: 0 }}>
                          <i className="fa fa-lock"></i> Permission Preview
                        </h3>
                        {selectedRole && (
                          <span className="label bg-blue" style={{ fontSize: 12, padding: "4px 10px" }}>
                            {selectedRole.permissions?.length || 0} permissions across{" "}
                            {Object.keys(groupedPerms).length} modules
                          </span>
                        )}
                      </div>

                      <div className="box-body">
                        {!selectedRole ? (
                          <div style={{ textAlign: "center", padding: "40px 20px", color: "#bbb" }}>
                            <i className="fa fa-shield" style={{ fontSize: 48, marginBottom: 12, display: "block" }}></i>
                            <p style={{ fontSize: 15, margin: 0 }}>
                              {roleId === ""
                                ? "No role selected — user will have full legacy access."
                                : "Select a role on the left to preview its permissions."}
                            </p>
                          </div>
                        ) : (
                          <>
                            {/* Stats row */}
                            <div className="ar-stat-row">
                              <div className="ar-stat">
                                <div className="ar-stat-num">
                                  {selectedRole.permissions?.length || 0}
                                </div>
                                <div className="ar-stat-label">Total Permissions</div>
                              </div>
                              <div className="ar-stat">
                                <div className="ar-stat-num">
                                  {Object.keys(groupedPerms).length}
                                </div>
                                <div className="ar-stat-label">Modules</div>
                              </div>
                              <div className="ar-stat">
                                <div className="ar-stat-num" style={{ color: "#00a65a" }}>
                                  {selectedRole.permissions?.filter((p) => p.endsWith(".view")).length || 0}
                                </div>
                                <div className="ar-stat-label">View Access</div>
                              </div>
                              <div className="ar-stat">
                                <div className="ar-stat-num" style={{ color: "#f39c12" }}>
                                  {selectedRole.permissions?.filter(
                                    (p) => p.endsWith(".edit") || p.endsWith(".create")
                                  ).length || 0}
                                </div>
                                <div className="ar-stat-label">Edit / Create</div>
                              </div>
                            </div>

                            {/* Grouped permissions */}
                            <div className="ar-perms-grid">
                              {Object.entries(groupedPerms).map(([mod, actions]) => (
                                <div key={mod} className="ar-perm-group">
                                  <div className="ar-perm-group-header">
                                    <i className="fa fa-folder-o"></i>
                                    {MODULE_LABELS[mod] || mod}
                                    <span
                                      className="badge"
                                      style={{
                                        background: "#ddd",
                                        color: "#666",
                                        fontSize: 10,
                                        marginLeft: "auto",
                                      }}
                                    >
                                      {actions.length}
                                    </span>
                                  </div>
                                  <div className="ar-perm-group-body">
                                    {actions.map((action) => (
                                      <span
                                        key={action}
                                        className="ar-perm-tag"
                                        style={{
                                          borderLeft: `3px solid ${ACTION_COLORS[action] || "#ccc"}`,
                                        }}
                                      >
                                        <i
                                          className={`fa ${ACTION_ICONS[action] || "fa-circle"}`}
                                          style={{
                                            fontSize: 9,
                                            color: ACTION_COLORS[action] || "#999",
                                          }}
                                        ></i>
                                        {action}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default AssignRole;
